<template>
  <aside
    class="template-preview-pane"
    :class="{ 'is-fullscreen': fullscreen }"
  >
    <div class="preview-toolbar">
      <div class="preview-toolbar__title">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
        <span>Live preview</span>
        <span v-if="syncing" class="preview-toolbar__syncing">syncing…</span>
        <span v-if="currentWidthLabel" class="preview-toolbar__dim">{{ currentWidthLabel }}</span>
      </div>

      <div class="preview-toolbar__viewports" role="group" aria-label="Viewport size">
        <button
          v-for="vp in viewports"
          :key="vp.key"
          type="button"
          :title="`${vp.label} (${vp.width || 'fit'}px)`"
          :aria-label="`${vp.label} viewport`"
          :aria-pressed="viewport === vp.key"
          :class="['vp-btn', { 'vp-btn--active': viewport === vp.key }]"
          @click="viewport = vp.key"
        >
          <span aria-hidden="true" v-html="vp.icon"></span>
          <span class="vp-btn__label">{{ vp.label }}</span>
        </button>
      </div>

      <div class="preview-toolbar__actions">
        <button
          type="button"
          class="tool-btn"
          :aria-label="fullscreen ? 'Exit full-screen preview' : 'Expand to full screen'"
          :title="fullscreen ? 'Exit full screen (Esc)' : 'Expand to full screen'"
          @click="toggleFullscreen"
        >
          <svg v-if="!fullscreen" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="15 3 21 3 21 9"/>
            <polyline points="9 21 3 21 3 15"/>
            <line x1="21" y1="3" x2="14" y2="10"/>
            <line x1="3" y1="21" x2="10" y2="14"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="4 14 10 14 10 20"/>
            <polyline points="20 10 14 10 14 4"/>
            <line x1="14" y1="10" x2="21" y2="3"/>
            <line x1="3" y1="21" x2="10" y2="14"/>
          </svg>
        </button>
      </div>
    </div>

    <div class="preview-stage">
      <div
        ref="frameWrap"
        class="preview-frame-wrap"
        :class="{
          'is-framed': viewport !== 'desktop',
          'is-scaled': desktopScale < 1,
        }"
      >
        <iframe
          ref="frame"
          :src="src"
          class="preview-frame"
          :class="`preview-frame--${viewport}`"
          :style="frameStyle"
          title="Template preview"
          @load="onFrameLoad"
        ></iframe>
      </div>
    </div>
  </aside>
</template>

<script>
// Inline SVG icons for viewport toggle — matches the editor's stroke-based glyph language.
const svg = (d) => `<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
const VIEWPORTS = [
  { key: 'mobile',  label: 'Mobile',  width: 390,  icon: svg('<rect x="7" y="2" width="10" height="20" rx="2"/><line x1="11" y1="18" x2="13" y2="18"/>') },
  { key: 'tablet',  label: 'Tablet',  width: 820,  icon: svg('<rect x="3" y="4" width="18" height="16" rx="2"/><line x1="10" y1="17" x2="14" y2="17"/>') },
  { key: 'desktop', label: 'Desktop', width: null, icon: svg('<rect x="2" y="4" width="20" height="12" rx="2"/><line x1="8" y1="20" x2="16" y2="20"/><line x1="12" y1="16" x2="12" y2="20"/>') },
];

export default {
  name: 'TemplatePreviewPane',

  props: {
    src: { type: String, required: true },
    content: { type: Object, required: true },
    activeSection: { type: String, default: '' },
    /**
     * Optional step index passed by section editors that have a per-step
     * preview (e.g. SupportModalSectionEditor). When non-null and the
     * matching modal is active, the preview opens that step. Treated as
     * the canonical step for whichever modal is currently active.
     */
    previewStep: { type: Number, default: null },
  },

  data() {
    return {
      viewport: 'desktop',
      fullscreen: false,
      frameReady: false,
      syncing: false,
      viewports: VIEWPORTS,
      measuredWidth: 0,
    };
  },

  computed: {
    currentViewport() {
      return VIEWPORTS.find(v => v.key === this.viewport) || VIEWPORTS[2];
    },
    currentWidthLabel() {
      if (this.currentViewport.width) return `${this.currentViewport.width}px`;
      if (this.desktopForcesWideIframe) {
        return `${this.desktopForcedWidth}px @ ${(this.desktopScale * 100).toFixed(0)}%`;
      }
      return this.measuredWidth ? `${this.measuredWidth}px` : '';
    },
    /**
     * Sections that render inside a modal whose desktop layout uses a
     * media query that won't fire below 1024px (V2SupportModal switches
     * its split layout to stacked at ≤1023px, etc.). On those sections
     * the desktop preview iframe needs to be at least that wide
     * internally so the modal's @media rules read "desktop" — even when
     * the editor's pane is narrower (rail + form column eat ~512px on a
     * 1280px monitor, leaving ~768px for the preview pane).
     */
    isModalSection() {
      return this.activeSection === 'support_modal'
        || this.activeSection === 'donation_widget'
    },
    desktopForcedWidth() { return 1080 },
    desktopForcesWideIframe() {
      return this.viewport === 'desktop' && this.isModalSection
    },
    desktopScale() {
      if (!this.desktopForcesWideIframe || !this.measuredWidth) return 1
      // Only scale DOWN — at panes wider than 1080 we render 1:1 instead
      // of upscaling (which gets blurry).
      return Math.min(1, this.measuredWidth / this.desktopForcedWidth)
    },
    frameStyle() {
      // Modal-section desktop: render at fixed wide width and CSS-scale
      // to fit the pane. Width is the internal pixel size; height is in
      // % so it fills the stage box AFTER scaling (stage_height /
      // scale = iframe height needed for the post-transform size to
      // match the stage). transformOrigin:0,0 keeps the iframe pinned
      // to the top-left so we don't get whitespace gaps.
      if (this.desktopForcesWideIframe) {
        return {
          width: `${this.desktopForcedWidth}px`,
          height: `${100 / this.desktopScale}%`,
          transform: `scale(${this.desktopScale})`,
          transformOrigin: '0 0',
        }
      }
      // Plain desktop: iframe fills the canvas box.
      if (this.viewport === 'desktop') {
        return { width: '100%', height: '100%' };
      }
      // Mobile/tablet: fixed pixel width so `md:`/`lg:` breakpoints resolve against
      // the intended device width regardless of monitor size.
      return {
        width: `${this.currentViewport.width}px`,
      };
    },
  },

  watch: {
    content: {
      deep: true,
      handler() {
        this.syncContentDebounced();
      },
    },
    activeSection(key) {
      this.post({ type: 'section:scroll', sectionKey: key });
      this.syncModalState();
    },
    previewStep() {
      this.syncModalState();
    },
  },

  mounted() {
    window.addEventListener('message', this.onMessage);
    window.addEventListener('keydown', this.onKeyDown);
    this.observeFrameWidth();
  },

  beforeUnmount() {
    window.removeEventListener('message', this.onMessage);
    window.removeEventListener('keydown', this.onKeyDown);
    clearTimeout(this._syncTimer);
    this._resizeObserver?.disconnect();
  },

  methods: {
    onFrameLoad() {
      // Ready flag is set by handshake (preview:ready message), but also set
      // here as a fallback so initial content is definitely sent even if the
      // handshake is missed.
      this.frameReady = true;
      this.syncContent();
      this.syncModalState();
    },

    onMessage(event) {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.source !== 'template-preview') return;

      if (data.type === 'preview:ready') {
        this.frameReady = true;
        this.syncContent();
        this.syncModalState();
      }
    },

    /**
     * When the admin focuses a section that is rendered inside a modal
     * (Support Modal, Donate Copy), tell the preview to open the matching
     * modal so they can see the override apply live. Sections that don't
     * involve a modal close any open one.
     */
    syncModalState() {
      if (!this.frameReady) return;
      let modal = null;
      if (this.activeSection === 'support_modal') modal = 'support';
      else if (this.activeSection === 'donation_widget') modal = 'donation';
      if (modal) {
        this.post({
          type: 'preview:open-modal',
          modal,
          step: typeof this.previewStep === 'number' ? this.previewStep : undefined,
        });
      } else {
        this.post({ type: 'preview:close-modals' });
      }
    },

    onKeyDown(e) {
      if (e.key === 'Escape' && this.fullscreen) {
        this.fullscreen = false;
      }
    },

    toggleFullscreen() {
      this.fullscreen = !this.fullscreen;
      // Re-push content after layout change so the iframe pick up any missed frames.
      this.$nextTick(this.syncContent);
    },

    observeFrameWidth() {
      // Observe the wrap rather than the iframe itself — when we apply
      // transform: scale() to the iframe for modal-section previews,
      // the iframe's layout box is no longer the pane width. The wrap
      // sits in flow at the actual pane width regardless.
      const target = this.$refs.frameWrap || this.$refs.frame;
      if (!target || typeof ResizeObserver === 'undefined') return;
      this._resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          this.measuredWidth = Math.round(entry.contentRect.width);
        }
      });
      this._resizeObserver.observe(target);
    },

    post(payload) {
      const frame = this.$refs.frame;
      if (!frame || !frame.contentWindow) return;
      // Deep-clone via JSON to strip Vue reactive Proxies — structured clone
      // (used internally by postMessage) cannot serialize them and throws
      // "could not be cloned". All template content is plain data so this
      // round-trip is lossless.
      const safe = JSON.parse(JSON.stringify({ source: 'template-editor', ...payload }));
      frame.contentWindow.postMessage(safe, window.location.origin);
    },

    syncContent() {
      if (!this.frameReady) return;
      this.syncing = true;
      this.post({ type: 'content:update', content: this.content });
      // Visual "syncing…" indicator is purely cosmetic — fade it out quickly.
      clearTimeout(this._syncFlashTimer);
      this._syncFlashTimer = setTimeout(() => { this.syncing = false; }, 250);
    },

    syncContentDebounced() {
      clearTimeout(this._syncTimer);
      this._syncTimer = setTimeout(this.syncContent, 150);
    },
  },
};
</script>

<style scoped>
.template-preview-pane {
  --pv-ink:   #0f172a;
  --pv-muted: #64748b;
  --pv-line:  #e2e8f0;
  --pv-surface: #ffffff;
  --pv-canvas: #f1f5f9;
  --pv-accent: #db2777;

  display: flex;
  flex-direction: column;
  background: var(--pv-canvas);
  min-height: 0;
  position: relative;
}

.template-preview-pane.is-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 80;
  background: var(--pv-canvas);
}

/* ─────────────── Toolbar ─────────────── */
.preview-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.55rem 0.9rem;
  background: var(--pv-surface);
  border-bottom: 1px solid var(--pv-line);
  flex-shrink: 0;
  z-index: 2;
}
.preview-toolbar__title {
  font-weight: 600;
  color: var(--pv-ink);
  font-size: 0.78rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  flex-shrink: 0;
  letter-spacing: 0.005em;
}
.preview-toolbar__syncing {
  font-weight: 400;
  color: var(--pv-muted);
  font-size: 0.7rem;
  font-style: italic;
}
.preview-toolbar__dim {
  margin-left: 0.35rem;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  background: var(--pv-canvas);
  color: var(--pv-muted);
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.005em;
}

.preview-toolbar__viewports {
  display: inline-flex;
  gap: 2px;
  padding: 2px;
  background: var(--pv-canvas);
  border-radius: 8px;
  margin-left: auto;
  border: 1px solid var(--pv-line);
}
.vp-btn {
  background: transparent;
  border: none;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 0.72rem;
  color: var(--pv-muted);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;
  font-weight: 500;
  line-height: 1;
}
.vp-btn:hover {
  color: var(--pv-ink);
}
.vp-btn--active {
  background: var(--pv-surface);
  color: var(--pv-ink);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}
.vp-btn__label {
  font-weight: 500;
}

.preview-toolbar__actions {
  display: inline-flex;
  gap: 3px;
}
.tool-btn {
  background: transparent;
  border: 1px solid var(--pv-line);
  border-radius: 6px;
  width: 30px;
  height: 30px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--pv-muted);
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.tool-btn:hover {
  background: var(--pv-canvas);
  color: var(--pv-ink);
  border-color: #cbd5e1;
}

/* ─────────────── Stage ─────────────── */
.preview-stage {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: var(--pv-canvas);
}

.preview-frame-wrap {
  flex: 1;
  min-height: 0;
  overflow: auto;
  background: var(--pv-canvas);
  display: flex;
  justify-content: center;
  align-items: stretch;
}

/* When the preview is scaled (modal-section desktop preview), the
   iframe sits at fixed 1080px and uses transform: scale to fit.
   Hidden overflow keeps the scaled iframe from spilling past the pane;
   block layout (no flex) avoids fighting the iframe's percent height. */
.preview-frame-wrap.is-scaled {
  overflow: hidden;
  display: block;
  position: relative;
}
.preview-frame-wrap.is-scaled .preview-frame {
  display: block;
  border: 0;
}

.preview-frame-wrap.is-framed {
  /* Non-desktop viewports: show the iframe as a device-shaped card
     centered in a dotted-grid canvas for visual context. */
  padding: 1.5rem;
  background-color: var(--pv-canvas);
  background-image: radial-gradient(circle, #cbd5e1 1px, transparent 1px);
  background-size: 14px 14px;
  align-items: flex-start;
}

.preview-frame {
  display: block;
  border: 0;
  background: var(--pv-surface);
  height: 100%;
  width: 100%;
}

/* Device-framed iframes sit on a card with rounded corners + shadow. */
.preview-frame--mobile,
.preview-frame--tablet {
  flex-shrink: 0;
  height: auto;
  min-height: calc(100vh - 160px);
  border-radius: 12px;
  box-shadow:
    0 0 0 1px rgba(15, 23, 42, 0.06),
    0 10px 30px -10px rgba(15, 23, 42, 0.25),
    0 4px 12px -4px rgba(15, 23, 42, 0.1);
}

/* Fullscreen mode: device frames have more vertical room */
.is-fullscreen .preview-frame--mobile,
.is-fullscreen .preview-frame--tablet {
  min-height: calc(100vh - 100px);
}
</style>
